<template>
  <ReportModuleTable
    title="Layouts cadastrados"
    subtitle="Modelos visuais disponíveis para relatórios técnicos."
    :columns="columns"
    :items="layouts"
  >
    <template #empty>
      <ReportEmptyState
        title="Nenhum layout cadastrado"
        description="Cadastre layouts para personalizar capa, cabeçalho, rodapé, marca d’água e identidade visual dos relatórios."
        :icon="PhSlidersHorizontal"
      />
    </template>

    <template #cell-name="{ item }">
      <div>
        <div class="flex items-center gap-2">
          <p class="font-semibold text-gray-800">
            {{ item.name }}
          </p>

          <span
            v-if="item.is_default"
            class="inline-flex items-center gap-1 rounded-full border border-yellow-100 bg-yellow-50 px-2.5 py-1 text-xs font-semibold text-yellow-700"
          >
            <PhStar class="h-3.5 w-3.5" />
            Padrão
          </span>
        </div>

        <p class="mt-0.5 text-xs text-gray-500">
          {{ item.description || "Sem descrição" }}
        </p>
      </div>
    </template>

    <template #cell-format="{ item }">
      <span class="font-medium text-gray-700">
        {{ getFormatLabel(item.page_format) }}
      </span>

      <p class="text-xs text-gray-400">
        {{ getOrientationLabel(item.orientation) }}
      </p>
    </template>

    <template #cell-files="{ item }">
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="file in getFileBadges(item)"
          :key="file.label"
          class="rounded-full px-2.5 py-1 text-xs font-semibold"
          :class="
            file.active
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-400'
          "
        >
          {{ file.label }}
        </span>
      </div>
    </template>

    <template #cell-status="{ item }">
      <span
        class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
        :class="
          item.is_active
            ? 'border-green-100 bg-green-50 text-green-700'
            : 'border-gray-200 bg-gray-100 text-gray-500'
        "
      >
        {{ item.is_active ? "Ativo" : "Inativo" }}
      </span>
    </template>

    <template #actions="{ item }">
      <div class="flex items-center justify-center gap-2">
        <button
          type="button"
          title="Configurar layout"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('configure', item)"
        >
          <PhSlidersHorizontal class="h-5 w-5" />
        </button>
        
        <button
          type="button"
          title="Editar layout"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-yellow-50 hover:text-yellow-600"
          @click="$emit('edit', item)"
        >
          <PhNotePencil class="h-5 w-5" />
        </button>

        <button
          v-if="item.is_active && !item.is_default"
          type="button"
          title="Definir como padrão"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-yellow-50 hover:text-yellow-700"
          @click="$emit('set-default', item)"
        >
          <PhStar class="h-5 w-5" />
        </button>

        <button
          v-if="item.is_active"
          type="button"
          title="Inativar layout"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
          @click="$emit('disable', item)"
        >
          <PhPower class="h-5 w-5" />
        </button>

        <button
          v-else
          type="button"
          title="Reativar layout"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('enable', item)"
        >
          <PhCheckCircle class="h-5 w-5" />
        </button>
      </div>
    </template>
  </ReportModuleTable>
</template>

<script setup>
import {
  PhCheckCircle,
  PhNotePencil,
  PhPower,
  PhSlidersHorizontal,
  PhStar,
} from "@phosphor-icons/vue";

import ReportModuleTable from "../shared/ReportModuleTable.vue";
import ReportEmptyState from "../shared/ReportEmptyState.vue";

defineProps({
  layouts: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["configure", "edit", "disable", "enable", "set-default"]);

const columns = [
  {
    key: "name",
    label: "Layout",
  },
  {
    key: "format",
    label: "Formato",
  },
  {
    key: "files",
    label: "Arquivos",
  },
  {
    key: "status",
    label: "Status",
    align: "center",
  },
];

function getFormatLabel(format) {
  const labels = {
    a4: "A4",
    letter: "Carta",
  };

  return labels[format] || "A4";
}

function getOrientationLabel(orientation) {
  const labels = {
    portrait: "Retrato",
    landscape: "Paisagem",
  };

  return labels[orientation] || "Retrato";
}

function getFileBadges(layout) {
  return [
    {
      label: "Capa",
      active: Boolean(layout.cover_url || layout.cover_path),
    },
    {
      label: "Cabeçalho",
      active: Boolean(layout.header_url || layout.header_path),
    },
    {
      label: "Rodapé",
      active: Boolean(layout.footer_url || layout.footer_path),
    },
    {
      label: "Marca d’água",
      active: Boolean(layout.watermark_url || layout.watermark_path),
    },
  ];
}
</script>
